import Vue from 'vue'
import firebase from 'firebase/app'
import 'firebase/database'
import config from '@/config/config'
import { RESOURCE_TYPES } from '@/constants/general'
import { TEST_STATES, TEST_METHODS } from '@/constants/tests'
import testAudiences from '@/data/testAudiences'
import { functions } from '@/firebase'
import { normaliseDomain } from '@/utils/misc'
import { createdProjectAction, deletedProjectAction, duplicatedProjectAction, publishedProjectAction } from '@/utils/logsHelpers'

const testEnterpriseId = (owner, user) => {
  return owner ? ((owner.enterprise && owner.enterprise.id) || false) : ((user.enterprise && user.enterprise.id) || false)
}

const isTestEnterprise = (owner, user) => {
  return !!testEnterpriseId(owner, user)
}
const localDevServer = 'localhost:8080'
const snapshotParser = (snapshotsObject) => {
  if (snapshotsObject && Object.keys(snapshotsObject).length !== 0) {
    return Object.keys(snapshotsObject).map(x => {
      return {
        ...snapshotsObject[x],
        id: x
      }
    })
  }
  return []
}
export default {
  namespaced: true,
  state: {
    tests: {},
    testsRef: null,
    teamTests: {},
    teamTestsRef: null,
    testSubscribers: [],
    testCampaigns: {},
    testAudiences: testAudiences,

    isLoadingTests: true,
    isLoadingTeamTests: true,
    loadingTestsUserId: null,
    loadingTestsEnterpriseId: null,

    loadedTestId: null,
    loadedTest: null,
    loadedTestRef: null,
    loadedExperience: null,
    loadedPage: null,
    loadedSessions: null,
    loadedSessionsRef: null,
    loadedListenerTimer: null,
    loadedListenerCount: 0,

    domains: {},
    domainsRef: null,

    webhooks: {},
    webhookRef: null,
    savedReports: {},
    allLoadedExperiences: {},
    totalCreatedTests: 0,
    qrCodesData: null,
    badgeConfimationInProgress: false,
    testAuthorPlan: null,
    micrositeAuthorAccountStatus: null
    // for pagination
    // lastTest: '',
    // testsToPaginate: null
  },
  getters: {
    getTestAuthorPlan (state) {
      return state.testAuthorPlan
    },
    getBadgeConfrimationStatus (state) {
      return state.badgeConfimationInProgress
    },
    getSavedReports (state) {
      return state.savedReports
    },
    tests (state) {
      return state.tests
    },
    testsArray (state) {
      return snapshotParser(state.tests)
    },
    teamTests (state) {
      return state.teamTests
    },
    test (state) {
      return (id) => {
        return state.tests[id] || state.teamTests[id]
      }
    },
    isLoadingTests (state) {
      return state.isLoadingTests || state.isLoadingTeamTests
    },
    testSubscribers (state) {
      return state.testSubscribers
    },
    testCampaign (state) {
      return testId => state.testCampaigns[testId]
    },
    testAudiences (state) {
      return state.testAudiences
    },

    loadedTest (state) {
      return state.loadedTest
    },
    loadedExperience (state) {
      return state.loadedExperience
    },
    loadedSessions (state) {
      return state.loadedSessions
    },

    domains (state) {
      return state.domains
    },
    webhooks (state) {
      return state.webhooks
    },
    getAllLoadedExperiences (state) {
      return state.allLoadedExperiences
    },
    getTotalCreatedTests (state) {
      return state.totalCreatedTests
    },
    getQrCodesData (state) {
      return state.qrCodesData
    }
  },
  mutations: {
    setTests (state, payload) {
      if (payload) state.tests = payload
      else state.tests = {}
      state.isLoadingTests = false
    },
    setTestsRef (state, payload) {
      state.testsRef = payload
    },
    setTeamTests (state, payload) {
      if (payload) state.teamTests = payload
      else state.teamTests = {}
      state.isLoadingTeamTests = false
    },
    setTeamTestsRef (state, payload) {
      state.teamTestsRef = payload
    },
    setLoadingTestsUserId (state, payload) {
      state.loadingTestsUserId = payload
    },
    setLoadingTestsEnterpriseId (state, payload) {
      state.loadingTestsEnterpriseId = payload
    },
    updateTest (state, { testId, test }) {
      if (state.tests[testId] != null) {
        Vue.set(state.tests, testId, test)
      }
      if (state.teamTests[testId] != null) {
        Vue.set(state.teamTests, testId, test)
      }
    },
    removeTest (state, { testId }) {
      if (state.tests[testId] != null) {
        Vue.delete(state.tests, testId)
      }
      if (state.teamTests[testId] != null) {
        Vue.delete(state.teamTests, testId)
      }
    },
    setTestSubscribers (state, payload) {
      state.testSubscribers = payload
    },
    setTestCampaign (state, { testId, campaign }) {
      state.testCampaigns = {
        ...state.testCampaigns,
        [testId]: campaign
      }
    },

    clearLoadedTest (state) {
      state.loadedTestId = null
      state.loadedTest = null
      state.loadedExperience = null
      state.loadedPage = null
      state.loadedSessions = null
    },
    setLoadedTest (state, { id, test }) {
      state.loadedTestId = id
      state.loadedTest = test
    },
    setLoadedTestRef (state, payload) {
      state.loadedTestRef = payload
    },
    setLoadedSessions (state, payload) {
      state.loadedSessions = payload
    },
    setLoadedSessionsRef (state, payload) {
      state.loadedSessionsRef = payload
    },
    setLoadedExperience (state, payload) {
      state.loadedExperience = payload
    },
    setLoadedPage (state, payload) {
      state.loadedPage = payload
    },
    setLoadedListenerTimer (state, payload) {
      state.loadedListenerTimer = payload
    },
    incrementLoadedListeners (state) {
      state.loadedListenerCount++
    },
    decrementLoadedListeners (state) {
      state.loadedListenerCount--
    },
    setDomains (state, payload) {
      state.domains = payload
    },
    setDomainsRef (state, payload) {
      state.domainsRef = payload
    },
    setWebhooks (state, payload) {
      state.webhooks = payload
    },
    setWebhookRef (state, payload) {
      state.webhookRef = payload
    },
    saveLoadedReport (state, payload) {
      if (!state.savedReports[payload.projectId]) state.savedReports[payload.projectId] = {}
      state.savedReports[payload.projectId][payload.versionId] = payload.report
    },
    setAllLoadedExperiences (state, experiences) {
      state.allLoadedExperiences = { ...state.allLoadedExperiences, ...experiences }
    },
    setTotalCreatedTests (state, totalCreatedTests) {
      state.totalCreatedTests = totalCreatedTests
    },
    setQrCodeData (state, qrCodesData) {
      state.qrCodesData = qrCodesData
    },
    setBadgeConfirmationProgress (state) {
      state.badgeConfimationInProgress = true
    },
    setTestAuthorPlan (state, plan) {
      state.testAuthorPlan = plan
    },
  },
  actions: {
    async getQRCodeForUrl ({ commit }, { url }) {
      const qrCodesData = await functions.httpsCallable('qrCodes-getQRCodeForUrl')({ sourceURL: url })
      commit('setQrCodeData', qrCodesData.data)
    },
    async confirmTapkitBadgeStatus ({ commit }, { userId }) {
      commit('setBadgeConfirmationProgress')
      const userPlanResponse = await functions.httpsCallable('confrimTapkitBadgeStatus')({ userId })
      commit('setTestAuthorPlan', userPlanResponse.data)
    },
    async getTestSessionsOnce ({  }, testId) {
      const snapshot = await firebase
        .database()
        .ref('experience_session')
        .orderByChild('test')
        .equalTo(testId)
        .once('value')

      return snapshot.val()
    },
    async addTest ({  rootState, rootGetters }, { test, owner }) {
      if (rootState.auth.user) {
        var newTest = {
          ...test,
          __type: RESOURCE_TYPES.TEST,
          user_uid: owner ? owner.uid : rootState.auth.user.uid,
          user_username: (owner ? owner.username : rootState.auth.user.username) || '',
          user_enterprise: testEnterpriseId(owner, rootState.auth.user),
          state: TEST_STATES.EDITING,
          date: Date.now(),
          experience_config: { branding: (!rootGetters['wallet/usersFeatures']['remove-faraday-branding'] && !isTestEnterprise(owner, rootState.auth.user)) }
        }
        const testRef = await firebase
          .database()
          .ref('concept_tests')
          .push(newTest)

        newTest.id = testRef.key

        createdProjectAction({
          username: newTest.username,
          email: rootState.auth.user.email,
          uid: newTest.user_uid
        }, {
          ...newTest,
          message: `Project ${newTest.id} created`
        })

        return newTest
      }
    },
    async duplicateTest ({ dispatch, rootState }, { test, owner }) {
      const newTest = await dispatch('addTest', {
        test: {
          ...test,
          name: 'Copy of ' + test.name,
          budgetUsed: null,
          clicks: null,
          impressions: null,
          leads: null,
          score: null,
          method_results: null,
          custom_slug: null,
          domain: null,
          shopify: test.shopify ? true : null,
          domain_slug: null,
          adImage: null,
          unfurl: null,
          enterprise: null,
          versions: null,
          editingCopy: null
        },
        owner
      })
      duplicatedProjectAction(
        rootState.auth.user,
        { ...newTest, parent_test_id: test.id, test_id: newTest.id, message: `Project ${test.id} was duplicated to ${newTest.id}` })
      await dispatch('loadTests')
      return newTest.id
    },
    async updateTest ({ commit }, { testId, update }) {
      if (!testId) return
      // Update the test in firebase
      await firebase
        .database()
        .ref('concept_tests')
        .child(testId)
        .update(update)

      // Then retrieve result and update in the Vuex store
      const snap = await firebase
        .database()
        .ref('concept_tests')
        .child(testId)
        .once('value')

      const updatedTest = snap.val()
      commit('updateTest', { testId, test: updatedTest })
      return updatedTest
    },
    async publishTest ({ rootState, dispatch, rootGetters }, { testId, test, paymentMethod }) {
      var method = rootGetters.testMethods[test.method]
      var usersProduct = rootGetters['wallet/usersSubscriptionProduct']
      const userNeedsPremium = method.premium && !(rootGetters['wallet/userIsEnterprise'] || (usersProduct && usersProduct.features && usersProduct.features['premium-methods']))
      if (!test.DEMO && method && !userNeedsPremium) {
        // Attempt to charge the user's saved billing card
        // (Should only ever be called after verifying user has a card saved)

        var chargeResult = await dispatch('wallet/chargeForTest', {
          testId,
          test,
          description: `Running test: ${test.name}`,
          paymentMethod: paymentMethod
        }, { root: true })

        publishedProjectAction(rootState.auth.user, { ...test, test_id: testId, message: `Published ${testId} on ${test.custom_slug}` })

        return chargeResult
      } else if (userNeedsPremium) {
        publishedProjectAction(rootState.auth.user, { ...test, test_id: testId, message: 'A premium plan is required to publish this experience!' }, false)
        throw new Error('A premium plan is required to publish this experience!')
      } else {
        publishedProjectAction(rootState.auth.user, { ...test, test_id: testId, message: 'Unable to publish this test' }, false)
        throw new Error('Unable to publish this test')
      }
    },
    async deleteTest ({ rootState }, { testId }) {
      const testSnapshot = await firebase
        .database()
        .ref('concept_tests')
        .child(testId)
        .once('value')
      const test = testSnapshot.val()

      if (test.method === TEST_METHODS.CUSTOM && test.experience) {
        await firebase
          .database()
          .ref('sketch')
          .child(test.experience)
          .remove()
      }

      if (test.method === TEST_METHODS.PAGE && test.page) {
        await firebase
          .database()
          .ref('page')
          .child(test.page)
          .remove()
      }

      if (test.DEMO) {
        await firebase
          .database()
          .ref(`users/${rootState.auth.user.uid}/demo/deletedTests/${testId}`)
          .set(true)
      } else {
        await firebase
          .database()
          .ref('concept_tests')
          .child(testId)
          .remove()
      }
      deletedProjectAction(rootState.auth.user, { test_id: testId, message: `Deleted ${testId}` })
    },
    async removeDemo ({ rootState }, { testId }) {
      if (rootState.auth.user) {
        await firebase
          .database()
          .ref(`users/${rootState.auth.user.uid}/demo/deleted/${testId}`)
          .set(true)
      }
    },
    loadTests ({ state, commit, rootState, dispatch }) {
      const userId = rootState.auth.user && rootState.auth.user.uid
      const listening = !!state.testsRef

      if (listening && userId !== state.loadingTestsUserId) {
        // Make sure we only register one value listener at a time
        if (state.testsRef) state.testsRef.off()
        commit('setTestsRef', null)
      }
      if (userId && (!listening || userId !== state.loadingTestsUserId)) {
        commit('setLoadingTestsUserId', userId)
        const testsRef = firebase
          .database()
          .ref('concept_tests')
          .orderByChild('user_uid')
          .equalTo(userId)
        commit('setTestsRef', testsRef)

        testsRef.on('value', snapshot => {
          const tests = snapshot.val()
          if (tests) {
            commit('setTotalCreatedTests', Object.keys(tests).length)
          }
          commit('setTests', tests)
        })
      }

      dispatch('loadTeamTests')
    },
    loadTeamTests ({ state, commit, rootState }) {
      const enterpriseId = rootState.auth.user && rootState.auth.user.enterprise && rootState.auth.user.enterprise.id
      if (rootState.auth.user && !enterpriseId) commit('setTeamTests', {})
      else {
        const listening = !!state.teamTestsRef

        if (listening && enterpriseId !== state.loadingTestsEnterpriseId) {
          // Make sure we only register one value listener at a time
          state.teamTestsRef.off()
          commit('setTeamTestsRef', null)
        }
        if (enterpriseId && (!listening || enterpriseId !== state.loadingTestsEnterpriseId)) {
          commit('setLoadingTestsEnterpriseId', enterpriseId)
          const teamTestsRef = firebase
            .database()
            .ref('concept_tests')
            .orderByChild('enterprise')
            .equalTo(enterpriseId)
          commit('setTeamTestsRef', teamTestsRef)

          teamTestsRef.on('value', snapshot => {
            const tests = snapshot.val()
            commit('setTeamTests', tests)
          })
        }
      }
    },
    async getTest (context, testId) {
      if (!testId) return null
      const snapshot = await firebase
        .database()
        .ref('concept_tests')
        .child(testId)
        .once('value')
      return snapshot.val()
    },
    async getTestsForUser (context, userId) {
      const snapshot = await firebase
        .database()
        .ref('concept_tests')
        .orderByChild('user_uid')
        .equalTo(userId)
        .once('value')
      return snapshot.val()
    },
    async loadTestSubscribers ({ commit, rootState }) {
      var testSubscribers
      if (rootState.auth.user) {
        if (rootState.auth.user.roles && rootState.auth.user.roles.admin) {
          const snapshot = await firebase
            .database()
            .ref('concept_test_subscribers')
            .once('value')
          testSubscribers = snapshot.val()
          // commit('auth/setUsageStats', { field: 'leads', value: Object.keys(testSubscribers).length }, { root: true })
        } else {
          const snapshot = await firebase
            .database()
            .ref('concept_test_subscribers')
            .orderByChild('test_author')
            .equalTo(rootState.auth.user.username)
            .once('value')
          const snapshotValue = snapshot.val()
          const updatedSnapshot = await firebase
            .database()
            .ref('concept_test_subscribers')
            .orderByChild('author_username')
            .equalTo(rootState.auth.user.username)
            .once('value')
          const updatedSnapshotValue = updatedSnapshot.val()
          testSubscribers = { ...snapshotValue, ...updatedSnapshotValue }
          // commit('auth/setUsageStats', { field: 'leads', value: Object.keys(testSubscribers).length }, { root: true })
        }
      }
      commit('setTestSubscribers', testSubscribers)
    },
    async createTestSubscriber ({ rootGetters }, { subscriber, responseSummary }) {
      const now = Date.now()
      // strip out any undefined fields to keep firebase happy
      const sub = JSON.parse(JSON.stringify({ ...subscriber, sign_up_date: now }))
      sub.responseSummary = responseSummary

      const subId = await functions.httpsCallable('api-processEmailCapture')(sub)
      const method = rootGetters.testMethods[subscriber.test_method]

      if (method.shareMode) {
        await functions.httpsCallable('logTestLead', { id: subscriber.test_id })
      }

      return subId
    },
    async updateOrCreateCampaign ({ dispatch }, { testId, update }) {
      if (update.method_results && update.method_results instanceof Array) {
        // multiple campaigns per test
        update.method_results.forEach(async result => {
          const campaign = await dispatch('getCampaignFromId', result.campaignId)
          if (campaign && campaign.test_id === testId && campaign.test_variant === result.variant) {
            dispatch('updateCampaign', {
              id: campaign.id,
              update: {
                leads: result.leads
              }
            })
          } else if (campaign && campaign.test_id !== testId) {
            throw new Error('This campaign is being used by another test')
          } else if (!campaign) {
            await dispatch('createCampaign', {
              test_id: testId,
              test_variant: result.variant,
              id: result.campaignId,
              leads: result.leads,
              status: 'ACTIVE'
            })
          }
        })
      } else {
        // single campaign per test

        const campaign = await dispatch('getCampaignFromId', update.campaignId)
        if (campaign && campaign.test_id === testId) {
          dispatch('updateCampaign', {
            id: campaign.id,
            update: {
              leads: update.leads
            }
          })
        } else if (campaign && campaign.test_id !== testId) {
          throw new Error('This campaign is being used by another test')
        } else if (!campaign) {
          dispatch('createCampaign', {
            test_id: testId,
            test_variant: update.variant,
            id: update.campaignId,
            leads: update.leads,
            status: 'ACTIVE'
          })
        }
      }
    },
    async getTestBySlug ({}, { customSlug, host, shop }) {
      if (host && !config.LP_DOMAINS.includes(host) && host !== localDevServer) {
        const normalisedHost = normaliseDomain(host)
        const slugPath = customSlug ? `${normalisedHost}/slugs/${customSlug}` : `${normalisedHost}/root`
        const idSnap = await firebase.database().ref('domain').child(slugPath).once('value')
        const testId = idSnap.val()

        if (testId) {
          const snapshot = await firebase
            .database()
            .ref('concept_tests')
            .child(testId)
            .once('value')
          return {
            id: testId,
            test: snapshot.val()
          }
        } else return null
      } else {
        // Shopify-hosted
        let snapshot
        if (shop) {
          snapshot = await firebase
            .database()
            .ref('concept_tests')
            .orderByChild('shopify/key')
            .equalTo(`${shop}/${customSlug}`)
            .limitToFirst(1)
            .once('value')
        } else {
          if (customSlug === undefined && (host === 'tapk.it' || host === 'takit.me')) return null
          snapshot = await firebase
            .database()
            .ref('concept_tests')
            .orderByChild('custom_slug')
            .equalTo(customSlug)
            .limitToFirst(1)
            .once('value')
        }
        const result = snapshot.val()
        if (result) {
          const id = Object.keys(result)[0]
          const test = result[id]
          return {
            id,
            test
          }
        } else return null
      }
    },
    async checkCustomSlugIsFree ({}, { target, slug, id }) {
      if (target && target.domain) {
        const normalisedDomain = normaliseDomain(target.domain)
        const slugPath = slug ? `${normalisedDomain}/slugs/${slug}` : `${normalisedDomain}/root`
        const snapshot = await firebase
          .database()
          .ref('domain')
          .child(slugPath)
          .once('value')
        const testId = snapshot.val()
        return testId == null || testId === id
      } else {
        let snapshot
        if (target && target.shopify) {
          snapshot = await firebase
            .database()
            .ref('concept_tests')
            .orderByChild('shopify/key')
            .equalTo(`${target.shopify}/${slug}`)
            .limitToFirst(1)
            .once('value')
        } else {
          snapshot = await firebase
            .database()
            .ref('concept_tests')
            .orderByChild('custom_slug')
            .equalTo(slug)
            .limitToFirst(1)
            .once('value')
        }
        const result = snapshot.val()
        if (result) {
          const resultId = Object.keys(result)[0]
          const test = result[resultId]
          if (test && resultId !== id) return false
          else return true
        }
        return true
      }
    },
    async getSubscribersForTest ({ }, { testId }) {
      const snapshot = await firebase
        .database()
        .ref('concept_test_subscribers')
        .orderByChild('test_id')
        .equalTo(testId)
        .once('value')
      const testSubscribers = snapshot.val()
      return testSubscribers || {}
    },
    generateReport ({ }, { testId, experienceId }) {
      return functions.httpsCallable('getRawDataReport')({ testId, experienceId })
    },
    generateReportData ({ }, { testId, versionId }) {
      return functions.httpsCallable('report-generateReportData')({ projectId: testId, versionId })
    },
    saveReport ({ commit }, { report, versionId, projectId }) {
      commit('saveLoadedReport', { report, versionId, projectId })
    },
    async loadTestForReport ({ state, commit, dispatch }, { testId }) {
      commit('incrementLoadedListeners')
      if (state.loadedListenerTimer) {
        clearTimeout(state.loadedListenerTimer)
        commit('setLoadedListenerTimer', null)
      }
      if (!state.loadedTestRef || state.loadedTestId !== testId) {
        if (state.loadedTestRef) state.loadedTestRef.off()
        if (state.loadedSessionsRef) state.loadedSessionsRef.off()
        if (state.loadedTestId !== testId) {
          commit('clearLoadedTest')
        }

        const testRef = firebase
          .database()
          .ref('concept_tests')
          .child(testId)
        commit('setLoadedTestRef', testRef)

        const testSnapshot = await testRef.once('value')
        const loadedTest = testSnapshot.val()

        commit('setLoadedTest', {
          id: testId,
          test: loadedTest
        })

        if (loadedTest.method === TEST_METHODS.CUSTOM || loadedTest.method === TEST_METHODS.PAGE) {
          dispatch('loadExperienceForReport', { experienceId: loadedTest.experience })
        }
      }
    },
    // Delay unsubscribing to database listeners for 10s, to avoid teardown/setup costs when quickly switching between pages
    unloadTestForReport ({ state, commit }, { testId }) {
      if (state.loadedListenerTimer) {
        clearTimeout(state.loadedListenerTimer)
      }
      if (state.loadedListenerCount <= 1) {
        const timer = setTimeout(() => {
          commit('setLoadedListenerTimer', null)
          if (state.loadedTestRef && state.loadedTestId === testId) {
            state.loadedTestRef.off()
            state.loadedSessionsRef.off()
            commit('setLoadedTestRef', null)
            commit('setLoadedSessionsRef', null)
          }
        }, 5000)
        commit('setLoadedListenerTimer', timer)
      }
      commit('decrementLoadedListeners')
    },
    async loadExperienceForReport ({ commit }, { experienceId }) {
      const snapshot = await firebase
        .database()
        .ref('sketch')
        .child(experienceId)
        .once('value')
      commit('setLoadedExperience', snapshot.val())
    },
    async loadPageForReport ({ commit }, { pageId }) {
      const snapshot = await firebase
        .database()
        .ref('page')
        .child(pageId)
        .once('value')
      commit('setLoadedPage', snapshot.val())
    },
    async loadDomains ({ state, commit }, { enterprise, user }) {
      if (state.domainsRef) {
        state.domainsRef.off()
        commit('setDomainsRef', null)
      }
      let ref
      if (enterprise) {
        ref = firebase.database().ref('domain')
          .orderByChild('enterprise')
          .equalTo(enterprise)
      } else if (user) {
        ref = firebase.database().ref('domain')
          .orderByChild('user')
          .equalTo(user)
      }
      if (ref) {
        ref.on('value', snapshot => {
          commit('setDomains', snapshot.val())
        })
        commit('setDomainsRef', ref)
      }
    },
    async saveDomain (context, { userId, domain }) {
      return functions.httpsCallable('domains-addDomain')({ userId, domain })
    },
    async removeDomain (context, { domain }) {
      return functions.httpsCallable('domains-removeDomain')({ domain })
    },
    async loadWebhooks ({ state, commit }, { userId }) {
      if (state.webhookRef) {
        // Make sure we only register one value listener at a time
        state.webhookRef.off()
      }

      const webhookRef = firebase
        .database()
        .ref('webhooks')
        .child(userId)
      commit('setWebhookRef', webhookRef)

      webhookRef.on('value', snapshot => {
        const webhooks = snapshot.val()
        commit('setWebhooks', webhooks)
      })
    },
    async unloadWebhooks ({ state }) {
      if (state.webhookRef) {
        state.webhookRef.off()
        state.webhookRef = null
      }
    },
    async addWebhook (context, { url }) {
      return functions.httpsCallable('api-createWebhook')({ url })
    },
    async removeWebhook (context, { id }) {
      return functions.httpsCallable('api-removeWebhook')({ webhookId: id })
    },

    async shareWithTeam ({ dispatch, rootGetters }, { testId }) {
      const enterprise = rootGetters['auth/user'].enterprise
      if (testId && enterprise && enterprise.id) {
        dispatch('tags/shareTagsFromFlow', { testId: testId, enterpriseId: enterprise.id }, { root: true })

        return dispatch('updateTest', {
          testId,
          update: {
            enterprise: enterprise.id
          }
        })
      }
    },
    async makePrivate ({ dispatch }, { testId }) {
      dispatch('tags/unShareFlowTags', testId, { root: true })
      return dispatch('updateTest', {
        testId,
        update: {
          enterprise: null
        }
      })
    },
    async getFbPixelId (context, { userId }) {
      const result = await functions.httpsCallable('integrations-getFbPixelId')({ userId })
      return result.data
    }
  }
}
